/** Consulta de hoje como a planta e a lista "Agora" enxergam (vem de `salas.agora`). */
export type ConsultaDaSala = {
  id: string;
  salaId: string | null;
  startTime: string;
  endTime: string;
  status: string;
  startedAt?: string | Date | null;
  dentistId?: string | null;
  dentistName?: string | null;
  iniciais: string;
  /** Nulo quando quem vê não pode saber quem é o paciente (ou no modo recepção). */
  pacienteNome: string | null;
};

export type EstadoDaSala =
  | { tipo: "em-atendimento"; consulta: ConsultaDaSala; desde: string | null; passouDoHorario: boolean }
  | { tipo: "aguardando-inicio"; consulta: ConsultaDaSala; atrasada: boolean; pacienteChegou: boolean }
  | { tipo: "livre"; proxima: ConsultaDaSala | null; pacienteChegou: boolean };

/** Minutos de tolerância antes de "aguardando início" virar "atrasada". */
export const TOLERANCIA_DE_ATRASO = 10;
const PENDENTES = ["SCHEDULED", "CONFIRMED", "WAITING", "LATE"];

const minutos = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const hhmmDe = (d: string | Date) => {
  const x = new Date(d);
  return `${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}`;
};
const porInicio = (a: ConsultaDaSala, b: ConsultaDaSala) => a.startTime.localeCompare(b.startTime);

/** O que está acontecendo numa sala às `agora` ("HH:MM", relógio local), a partir das consultas dela no dia. */
export function estadoDaSala(consultas: ConsultaDaSala[], agora: string): EstadoDaSala {
  const emAtendimento = consultas.filter((c) => c.status === "IN_PROGRESS").sort(porInicio).at(-1);
  if (emAtendimento) {
    return {
      tipo: "em-atendimento",
      consulta: emAtendimento,
      desde: emAtendimento.startedAt ? hhmmDe(emAtendimento.startedAt) : null,
      passouDoHorario: agora >= emAtendimento.endTime,
    };
  }
  const pendentes = consultas.filter((c) => PENDENTES.includes(c.status)).sort(porInicio);
  const agoraNoHorario = pendentes.find((c) => c.startTime <= agora && agora < c.endTime);
  if (agoraNoHorario) {
    return {
      tipo: "aguardando-inicio",
      consulta: agoraNoHorario,
      atrasada: minutos(agora) - minutos(agoraNoHorario.startTime) >= TOLERANCIA_DE_ATRASO,
      pacienteChegou: agoraNoHorario.status === "WAITING",
    };
  }
  const proxima = pendentes.find((c) => c.startTime > agora) ?? null;
  return { tipo: "livre", proxima, pacienteChegou: proxima?.status === "WAITING" };
}

/** Estado de cada sala, mais quem está em atendimento sem sala nenhuma (aparece à parte). */
export function estadoDasSalas(salas: { id: string }[], consultas: ConsultaDaSala[], agora: string) {
  const porSala: Record<string, EstadoDaSala> = {};
  for (const s of salas) porSala[s.id] = estadoDaSala(consultas.filter((c) => c.salaId === s.id), agora);
  const semSala = consultas.filter((c) => !c.salaId && c.status === "IN_PROGRESS").sort(porInicio);
  return { porSala, semSala };
}

/** "Maria José da Silva" → "M.S."; nome de uma palavra só → "M.". */
export function iniciaisDoNome(nome: string) {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return "?";
  const letras = partes.length === 1 ? [partes[0]] : [partes[0], partes[partes.length - 1]];
  return letras.map((p) => `${p[0]!.toLocaleUpperCase("pt-BR")}.`).join("");
}
